import { Icon } from '@iconify/react'
import { Button } from '@/components/ui/button.jsx'
import { cn } from '@/lib/utils'

export const PlanCard = ({ plan, cycle, onPlanChosen, currentSubscription, ctaText, specielOfferText, isDowngrade }) => {

    const price = !plan.isCustom ? plan.cycles[cycle].price : null
    const yearlyTotal = cycle === 'annually' && price ? Math.round(price * 12) : null
    const isCurrentSubscriptionPlan = currentSubscription?.plan === plan._id
    const isCustom = plan.isCustom
    const preferred = plan.isPreferred

    return (
        <div
            className={cn(
                'relative mx-auto flex h-full w-full max-w-[18rem] flex-col rounded-2xl border p-6 transition-all duration-200',
                preferred
                    ? 'border-primary bg-primary/[0.03] shadow-xl shadow-primary/10 ring-1 ring-primary/30'
                    : 'border-foreground/15 bg-card shadow-md hover:border-foreground/30 hover:shadow-lg'
            )}
        >
            {preferred ? (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="rounded-full bg-primary px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-primary-foreground shadow-sm">
                        Most popular
                    </span>
                </div>
            ) : plan.badge ? (
                <span className="absolute right-4 top-4 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {plan.badge}
                </span>
            ) : null}

            {/* Name + description — description keeps a reserved height so the
                price (and everything below) lines up across cards regardless of
                how long each plan's description is. */}
            <div className="space-y-1">
                <h3 className="text-lg font-semibold text-foreground">{plan.name}</h3>
                <p className="min-h-[3.5rem] text-[13px] leading-snug text-muted-foreground">
                    {plan.description}
                </p>
            </div>

            {/* Price */}
            <div className="mt-5">
                {isCustom ? (
                    <div className="text-3xl font-semibold tracking-tight text-foreground">Custom</div>
                ) : (
                    <>
                        <div className="flex items-baseline gap-1">
                            <span className="text-4xl font-semibold tracking-tight tabular-nums text-foreground">${price}</span>
                            <span className="text-sm text-muted-foreground">/mo</span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                            {cycle === 'annually'
                                ? `Billed yearly${yearlyTotal ? ` · $${yearlyTotal}/yr` : ''}`
                                : 'Billed monthly'}
                        </p>
                    </>
                )}
            </div>

            {/* Headline metrics */}
            {plan.highlights?.length > 0 && (
                <div className="mt-5 space-y-px rounded-lg border border-border/70 bg-muted/30 px-3 py-1">
                    {plan.highlights.map((highlight, idx) => (
                        <div
                            key={idx}
                            className="flex items-baseline justify-between gap-2 border-b border-border/60 py-1.5 last:border-0"
                        >
                            <span className="text-xs uppercase tracking-wide text-muted-foreground">{highlight.label}</span>
                            <span className="text-sm font-semibold tabular-nums text-foreground">{highlight.value}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Features */}
            <ul className="mt-5 flex-1 space-y-2.5">
                {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                        <Icon icon="solar:check-circle-bold" className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span className="text-[13px] leading-snug text-foreground/90">{feature}</span>
                    </li>
                ))}
            </ul>

            {plan.generosityNote && (
                <p className="mt-3 text-xs leading-5 text-muted-foreground">{plan.generosityNote}</p>
            )}

            {/* CTA */}
            <div className="mt-6 space-y-2">
                {isCustom ? (
                    <Button asChild variant="default" className="w-full">
                        <a href="mailto:yair@upspring.ai?subject=Custom%20Plan%20Request">{ctaText}</a>
                    </Button>
                ) : (
                    <Button
                        disabled={isCurrentSubscriptionPlan || isDowngrade}
                        variant="default"
                        className="w-full"
                        onClick={() => onPlanChosen(plan, cycle)}
                    >
                        {ctaText}
                    </Button>
                )}
                {specielOfferText && (
                    <p className="text-center text-[11px] text-muted-foreground">{specielOfferText}</p>
                )}
            </div>
        </div>
    )
}
